import { useState, useEffect, useRef } from "react";
import API from "@/lib/api";
import { openCamera, openPhotoPicker } from "@/lib/camera";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Camera, Mic, MicOff, Check, Pencil, Trash2, Loader2, AlertTriangle, X, Plus, Minus
} from "lucide-react";
import { toast } from "sonner";
import { useAI } from "@/lib/ai";
import { AIResultNote } from "@/components/AIResultNote";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

function VoiceInput({ onTranscript, disabled }) {
  const [listening, setListening] = useState(false);
  const recognitionRef = useRef(null);

  const startListening = () => {
    if (!("webkitSpeechRecognition" in window || "SpeechRecognition" in window)) {
      toast.error("Voice input not supported in this browser");
      return;
    }
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = "en-US";
    recognition.onresult = (event) => {
      let transcript = "";
      for (let i = 0; i < event.results.length; i++) {
        transcript += event.results[i][0].transcript;
      }
      onTranscript(transcript);
    };
    recognition.onerror = () => setListening(false);
    recognition.onend = () => setListening(false);
    recognitionRef.current = recognition;
    recognition.start();
    setListening(true);
  };

  const stopListening = () => {
    if (recognitionRef.current) recognitionRef.current.stop();
    setListening(false);
  };

  return (
    <Button variant="outline" size="sm" onClick={listening ? stopListening : startListening}
      disabled={disabled}
      className={`text-xs ${listening ? "bg-red-50 border-red-300 text-red-700" : ""}`}
      data-testid="damage-voice-input">
      {listening ? <><MicOff className="w-3.5 h-3.5 mr-1" /> Stop</> : <><Mic className="w-3.5 h-3.5 mr-1" /> Voice</>}
    </Button>
  );
}

function DamageCard({ assessment, onApprove, onOverride, onDelete }) {
  const [mode, setMode] = useState("view");
  const [editText, setEditText] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleApprove = async () => {
    setSubmitting(true);
    try {
      await onApprove(assessment.id);
    } finally {
      setSubmitting(false);
    }
  };

  const handleOverride = async () => {
    if (!editText.trim()) { toast.error("Description cannot be empty"); return; }
    setSubmitting(true);
    try {
      await onOverride(assessment.id, editText.trim());
      setMode("view");
    } finally {
      setSubmitting(false);
    }
  };

  const currentDesc = assessment.final_description || assessment.ai_description;

  return (
    <Card className="border border-zinc-200 overflow-hidden" data-testid={`damage-card-${assessment.id}`}>
      <div className="relative">
        <img
          src={`${API_BASE}/api/photos/damage-assessment/${assessment.id}/image`}
          alt="Damage assessment"
          className="w-full h-48 object-cover bg-zinc-100"
          loading="lazy"
        />
        <button
          onClick={() => onDelete(assessment.id)}
          className="absolute top-2 right-2 p-1 bg-black/50 rounded-full hover:bg-black/70 transition-colors"
          data-testid={`delete-damage-${assessment.id}`}
        >
          <X className="w-3.5 h-3.5 text-white" />
        </button>
        {assessment.is_approved && (
          <Badge className="absolute top-2 left-2 bg-emerald-500 text-white text-[10px]">
            <Check className="w-3 h-3 mr-0.5" /> Approved
          </Badge>
        )}
        {!assessment.is_approved && (
          <Badge className="absolute top-2 left-2 bg-amber-500 text-white text-[10px]">
            Pending Approval
          </Badge>
        )}
      </div>
      <CardContent className="p-4 space-y-3">
        {mode === "view" ? (
          <>
            <div>
              <p className="text-[10px] text-zinc-400 uppercase tracking-wider font-bold mb-1">
                {assessment.description_source === "ai" ? "AI Damage Assessment" :
                 assessment.description_source === "ai_approved" ? "AI Assessment (Approved)" :
                 "Your Assessment"}
              </p>
              <p className="text-xs text-zinc-700 leading-relaxed whitespace-pre-line">
                {currentDesc}
              </p>
              {assessment.description_source?.startsWith("ai") && (
                <AIResultNote feature="damage" refId={assessment.id} text={currentDesc} />
              )}
            </div>

            {!assessment.is_approved && (
              <div className="flex gap-2 pt-2 border-t border-zinc-100">
                <Button size="sm" onClick={handleApprove} disabled={submitting || !currentDesc}
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white text-xs h-9"
                  data-testid={`approve-damage-${assessment.id}`}>
                  {submitting ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Check className="w-3.5 h-3.5 mr-1" /> Approve Assessment</>}
                </Button>
                <Button size="sm" variant="outline" onClick={() => { setMode("edit"); setEditText(currentDesc); }}
                  className="flex-1 text-xs h-9" data-testid={`edit-damage-${assessment.id}`}>
                  <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
                </Button>
              </div>
            )}
          </>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-[10px] text-zinc-500 font-bold uppercase">Edit Assessment</p>
              <div className="flex gap-1">
                <Button variant="outline" size="sm" className="text-[10px] h-6 px-2"
                  onClick={() => setEditText(prev => prev + "\n\nAdditional notes: ")}
                  data-testid="add-to-desc">
                  <Plus className="w-3 h-3 mr-0.5" /> Add
                </Button>
                <VoiceInput onTranscript={(text) => setEditText(prev => prev + " " + text)} disabled={submitting} />
              </div>
            </div>
            <Textarea
              value={editText}
              onChange={e => setEditText(e.target.value)}
              rows={8}
              className="text-xs font-mono"
              placeholder="Edit the damage description..."
              data-testid={`damage-edit-text-${assessment.id}`}
            />
            <p className="text-[10px] text-zinc-400">
              You can add to, edit, or remove any part of the assessment. Voice input appends to the end.
            </p>
            <div className="flex gap-2">
              <Button size="sm" onClick={handleOverride}
                disabled={submitting || !editText.trim()}
                className="flex-1 bg-zinc-900 hover:bg-zinc-800 text-white text-xs h-9"
                data-testid={`save-damage-${assessment.id}`}>
                {submitting ? <Loader2 className="w-3 h-3 animate-spin" /> : "Save & Approve"}
              </Button>
              <Button size="sm" variant="outline" onClick={() => setMode("view")} className="text-xs h-9">
                Cancel
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function DamageAssessment({ requestId }) {
  const { aiOn } = useAI();
  const [assessments, setAssessments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const fetchAssessments = async () => {
    try {
      const { data } = await API.get(`/photos/damage-assessment/job/${requestId}`);
      setAssessments(data.assessments);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    if (requestId) fetchAssessments();
  }, [requestId]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("request_id", requestId);

    try {
      await API.post("/photos/damage-assessment", formData, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 45000,
      });
      toast.success("Damage photo analyzed — review the detailed assessment");
      fetchAssessments();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to analyze damage photo");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
      if (cameraInputRef.current) cameraInputRef.current.value = "";
    }
  };

  const handleApprove = async (id) => {
    try {
      await API.post(`/photos/damage-assessment/${id}/approve`);
      toast.success("Assessment approved");
      fetchAssessments();
    } catch (err) {
      toast.error("Failed to approve");
    }
  };

  const handleOverride = async (id, description) => {
    try {
      await API.post(`/photos/damage-assessment/${id}/override`, { description });
      toast.success("Assessment updated and approved");
      fetchAssessments();
    } catch (err) {
      toast.error("Failed to update");
    }
  };

  const handleDelete = async (id) => {
    try {
      await API.delete(`/photos/damage-assessment/${id}`);
      toast.success("Assessment deleted");
      fetchAssessments();
    } catch (err) {
      toast.error("Failed to delete");
    }
  };

  const unapproved = assessments.filter(a => !a.is_approved).length;

  return (
    <div className="space-y-3" data-testid="damage-assessment-section">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-500" />
          <h4 className="text-sm font-heading font-semibold text-zinc-900">
            Damage Assessment ({assessments.length})
          </h4>
          {unapproved > 0 && (
            <Badge className="bg-amber-50 text-amber-700 border-amber-200 text-[10px]">
              {unapproved} needs approval
            </Badge>
          )}
        </div>
      </div>

      <p className="text-[10px] text-zinc-500 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
        {aiOn
          ? "Take photos of all damaged areas. AI will generate a detailed assessment. Review, edit if needed, and approve before starting work."
          : "Take photos of all damaged areas, describe the damage in each, and approve before starting work."}
      </p>

      {/* Upload buttons */}
      <div className="flex gap-2">
        <input ref={cameraInputRef} type="file" accept="image/*" capture="environment"
          className="hidden" onChange={handleUpload} data-testid="damage-camera-input" />
        <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp"
          className="hidden" onChange={handleUpload} data-testid="damage-file-input" />
        <Button size="sm" variant="outline" onClick={() => openCamera(cameraInputRef)}
          disabled={uploading} className="flex-1 text-xs h-9 border-amber-300 text-amber-700 hover:bg-amber-50"
          data-testid="take-damage-photo-button">
          {uploading ? (
            <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> {aiOn ? "AI Assessing Damage..." : "Uploading..."}</>
          ) : (
            <><Camera className="w-3.5 h-3.5 mr-1.5" /> Photo Damage</>
          )}
        </Button>
        <Button size="sm" variant="outline" onClick={() => openPhotoPicker(fileInputRef)}
          disabled={uploading} className="flex-1 text-xs h-9"
          data-testid="upload-damage-photo-button">
          Upload Image
        </Button>
      </div>

      {/* Assessment cards */}
      {assessments.length > 0 && (
        <div className="space-y-3">
          {assessments.map(a => (
            <DamageCard key={a.id} assessment={a}
              onApprove={handleApprove} onOverride={handleOverride} onDelete={handleDelete} />
          ))}
        </div>
      )}

      {assessments.length === 0 && !uploading && (
        <div className="text-center py-4 border border-dashed border-amber-200 rounded-xl bg-amber-50/30">
          <AlertTriangle className="w-6 h-6 text-amber-300 mx-auto mb-1.5" />
          <p className="text-xs text-amber-600">No damage photos yet</p>
          <p className="text-[10px] text-amber-500 mt-0.5">Document all damage areas before starting repairs</p>
        </div>
      )}
    </div>
  );
}
